<template lang="pug">

.leaps
  section.leaps-intro
    AppTitle.u-mb-sm(:title="$t('mantra')")

    AppLink(to="https://github.com/baianat/leaps" class="button-link" :title="$t('github')") {{$t('github')}}
    img.leaps-intro-img(src='https://cdn.baianat.com/static/resources/leaps/1.png', srcset='https://cdn.baianat.com/static/resources/leaps/1.png 1x, https://cdn.baianat.com/static/resources/leaps/1@2x.png 2x')

  section.leaps-features
    .grid.is-desktop-6.is-mb
      .column
        Parallax(:from="{ translateY: 0 }" :to="{ translateY: 100  }")
          .leaps-card(slot-scope="{ parallax }" :style="`transform: translateY(${parallax.translateY}px)`")
            h4 {{$t('engagingAnimations')}}
            p {{$t('engagingAnimationsDesc')}}
      .column
        Parallax(:from="{ translateY: 0 }" :to="{ translateY: -100  }")
          .leaps-card.is-yellow(slot-scope="{ parallax }" :style="`transform: translateY(${parallax.translateY}px)`")
            h4 {{$t('declarativeSyntax')}}
            p {{$t('declarativeSyntaxDesc')}}

      .column
        Parallax(:from="{ translateY: 0 }" :to="{ translateY: 150  }")
          .leaps-card.is-blue(slot-scope="{ parallax }" :style="`transform: translateY(${parallax.translateY}px)`")
            h4 {{$t('customizable')}}
            p {{$t('customizableDesc')}}

      .column
        Parallax(:from="{ translateY: 0 }" :to="{ translateY: -150  }")
          .leaps-card.is-red(slot-scope="{ parallax }" :style="`transform: translateY(${parallax.translateY}px)`")
            h4 {{$t('parallaxScrolling')}}
            p {{$t('parallaxScrollingDesc')}}

  section.leaps-install.u-mt-lg
    h2 {{$t('install')}}
    p {{$t('installDesc')}}
    prism(language="js" :code="install")

  section.leaps-cta.u-mt-lg
    AppTitle(:title="$t('cta')" tag="h2")
    AppLink(to="https://baianat.github.io/leaps/getting-started.html" class="button-link" :title="$t('getStarted')") {{$t('getStarted')}}



  .u-mb-md

</template>
<style lang="stylus">
$clMain = #19002d
$clGreen = #00e2bd
$clYellow = #f1d500
$clBlue = #5410fd
$clRed = #ff002c

div.app.is-leaps
  background: $clMain
  color: $clWhite
  overflow: hidden

  &.is-dark
    .header.is-scroll
      background: $clMain

.leaps
  &-intro
    padding-top: unitRes(120, 200)
    z-index: 0
    position: relative

    &-img
      position: absolute
      top: -90%
      right: -45%
      z-index: -1

      +mq($until: 'desktop')
        top: -50%
        right: -55%

      +mq($until: 'tablet')
        top: -40%
        right: -35%

      +mq($until: 'mobile')
        display: none

  &-features
    margin-top: unitRes(180, 260)

  &-card
    background: $clWhite
    border: 10px solid $clGreen
    border-top-width: unitRes(20, 60)
    border-right-width: unitRes(20, 60)
    color: $clBlack
    padding: unitRes(40, 80)
    position: relative

    +mq($until: 'desktop')
      transform: none !important

    &:after
      content: ''
      position: absolute
      top: unitRes(-21, -61)
      left: -11px
      right: 0
      width: 0
      height: 0
      border-top: unitRes(10, 50) solid $clMain
      border-right: unitRes(10, 50) solid transparent

    &:before
      content: ''
      position: absolute
      bottom: -11px
      right: unitRes(-21, -61)
      width: 0
      height: 0
      border-right: unitRes(10, 50) solid $clMain
      border-top: unitRes(10, 50) solid transparent

    &.is-yellow
      border-color: $clYellow

    &.is-blue
      border-color: $clBlue

    &.is-red
      border-color: $clRed

  &-cta
    background-color: $clYellow
    padding: unitRes(20, 160)
    color: $clBlack
    background-image: url('https://cdn.baianat.com/static/resources/leaps/2.png')
    background-repeat: no-repeat
    background-position: right center
    background-attachment: fixed

html[lang^='ar']
  .leaps
    &-cta
      background-position: left center

    &-intro
      &-img
        right: auto
        left: -45%
        transform: scaleX(-1)
        filter: FlipH

        +mq($until: 'desktop')
          right: auto
          left: -55%

        +mq($until: 'tablet')
          right: auto
          left: -35%

    &-card
      border-right-width: 10px
      border-left-width: unitRes(20, 60)

      &:after
        right: -11px
        left: auto
        transform: rotate(90deg)

      &:before
        right: auto
        left: unitRes(-21, -61)
        transform: rotate(90deg)
</style>

<script>
import { Parallax } from 'leaps';
import Prism from '~/components/Prism';
import { generateSeoMeta } from '~/utils/seo';

export default {
  scrollToTop: true,
  meta: {
    theme: 'is-leaps is-dark',
    logoText: 'logoLeapsTypo',
    logoIcon: 'logoLeapsIcon'
  },

  head: generateSeoMeta(
    {
      title: 'pageTitle',
      description: 'pageDesc'
    },
    { i18n: true }
  ),
  components: {
    Prism,
    Parallax
  },
  data: () => ({
    install: `npm install leaps
# or use yarn
yarn add leaps
`
  })
};
</script>

<i18n>
{
  "en": {
    "pageTitle":"Leaps",
    "pageDesc":"Leaps is Baianat animation library that helps in creating custom web animation with minimal configuration and parallax scrolling effect.",
    "mantra":"Create awesome web |animations with Leaps.",
    "github":"Github",
    "engagingAnimations":"Engaging animations",
    "engagingAnimationsDesc":"Develop an interactive and engaging experience with your users through magical web animations.",
    "declarativeSyntax":"Declarative syntax",
    "declarativeSyntaxDesc":"Leaps offers minimal configuration and does not require you to write numerous lines of code",
    "customizable":"Customizable",
    "customizableDesc":"Customize your animation components using easy to edit props.",
    "parallaxScrolling":"Parallax scrolling",
    "parallaxScrollingDesc":"Create smooth parallax scrolling effects and configure its different properties.",
    "install":"Install",
    "installDesc":"First step is to install it using yarn or npm:",
    "cta":"Bring your |content to |life.",
    "getStarted":"Get started"
  },
  "ar": {
    "pageTitle":"Leaps",
    "pageDesc":"Leaps هو مكتبة للرسوم المتحركة التي تساعد في إنشاء الرسوم المتحركة مخصصة على الويب كما أنه يأتي مع تأثير parallax scrolling.",
    "mantra":"أنشأ أنيميشن مميز |على موقعك باستخدام Leaps ",
    "github":"غيت هاب",
    "engagingAnimations":"أنيميشن جذاب",
    "engagingAnimationsDesc":"صمم تجربة تفاعلية فريدة مع المستخدمين من خلال أنيميشن جذاب على موقعك.",
    "declarativeSyntax":"بناء تعريفي",
    "declarativeSyntaxDesc":"يقدم Leaps الحد الأدنى من التكوين كما أنه لا يتطلب منك كتابة العديد من الأسطر البرمجية.",
    "customizable":"مخصص",
    "customizableDesc":"خصص مكونات الأنيميشن الخاصة بك باستخدام أدوات تعديل سهلة.",
    "parallaxScrolling":"تمرير Parallax",
    "parallaxScrollingDesc":"يمكنك إنشاء تأثيرات التمرير السلس وتكوين خصائصه المختلفة.",
    "install":"التثبيت",
    "installDesc":"الخطوة الأولى هي تثبيته باستخدام yarn أو npm:",
    "cta":"أمنح محتواك |بعض الحياة.",
    "getStarted":"ابدأ الآن!"
  }
}
</i18n>
